import React, { createContext, useContext, useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  validateCandidateInterview,
  getInterviewQuestions,
  updateAnswer,
} from "../services/interviews";

const ProcessContext = createContext();

export const useProcess = () => {
  const context = useContext(ProcessContext);
  if (!context) {
    throw new Error("useProcess must be used within a ProcessProvider");
  }
  return context;
};

export const ProcessProvider = ({ children }) => {
  const { id } = useParams(); // Obtiene el :id de la URL
  const history = useHistory();

  const [state, setState] = useState({
    loading: false,
    processId: id,
    interviewId: null,
    interviewInfo: null,
    candidateId: null,
    candidateName: "",
    candidateMail: "",
    questions: [],
    currentQuestion: 0,
    expired: false,
    ready: false,
    error: null,
  });

  useEffect(() => {
    const loadProcessData = async () => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        // 1. Validar candidato y entrevista
        const aux = await validateCandidateInterview(id);

        const { candidate, interview, expired } = aux.data;

        if (expired) {
          history.push(`/processes/${id}/expired`);
          return;
        }

        // 2. Extraer información del candidato
        const candidateId = candidate.id;
        const candidateMail = candidate.mail;
        const candidateName = candidate.name;

        // 3. Obtener preguntas de la entrevista
        const questions = (
          await getInterviewQuestions(interview.id, candidateId)
        ).data;

        // 4. Calcular pregunta actual basado en nuevos estados
        let currentQuestion = 0;
        for (const question of questions) {
          // Verificar si la pregunta ya fue respondida (COMPLETE o INCOMPLETE)
          const hasAnsweredQuestion = question.answers.some(
            (answer) =>
              answer.state === "COMPLETE" || answer.state === "INCOMPLETE"
          );

          if (hasAnsweredQuestion) {
            currentQuestion += 1;
          } else {
            // Verificar si la pregunta está disponible para responder
            // (NOT_STARTED con tries <= questionMaxAttempts)
            const canAnswer = question.answers.some(
              (answer) =>
                (answer.state === "NOT_STARTED" || answer.state === "STARTED") &&
                (answer.tries || 0) <= interview.questionMaxAttempts
            );

            if (canAnswer) {
              break; // Esta es la próxima pregunta disponible
            } else {
              currentQuestion += 1; // Saltar preguntas que excedieron intentos
            }
          }
        }
        console.log("a. Pregunta actual calculada:", currentQuestion);
        console.log("a. Total de preguntas:", questions);

        // 5. Verificar si la entrevista está finalizada
        if (questions.length === currentQuestion) {
          history.push(`/processes/${id}/finished`);
          return;
        }

        // 6. Actualizar estado con toda la información
        setState((prev) => ({
          ...prev,
          loading: false,
          processId: id,
          interviewId: interview.id,
          interviewInfo: interview,
          candidateId,
          candidateMail,
          candidateName,
          questions,
          currentQuestion,
          expired,
          ready: true,
          error: null,
        }));
      } catch (error) {
        console.error("Error loading process data:", error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message,
          ready: false,
        }));

        // Si es error 500 o similar (candidateInterview no encontrado)
        if (error.response?.status >= 500) {
          history.push(`/processes/${id}/not-found`);
        }
      }
    };

    if (id) {
      loadProcessData();
    }
  }, [id]);

  // Función para actualizar el estado (útil para los componentes)
  const updateState = (updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  // Función para recalcular currentQuestion después de responder
  const updateCurrentQuestion = () => {
    let newCurrentQuestion = 0;
    for (const question of state.questions) {
      // Verificar si la pregunta ya fue respondida (COMPLETE o INCOMPLETE)
      const hasAnsweredQuestion = question.answers.some(
        (answer) => answer.state === "COMPLETE" || answer.state === "INCOMPLETE"
      );

      if (hasAnsweredQuestion) {
        newCurrentQuestion += 1;
      } else {
        // Verificar si la pregunta está disponible para responder
        // (NOT_STARTED con tries <= questionMaxAttempts)
        const canAnswer = question.answers.some(
          (answer) =>
            (answer.state === "NOT_STARTED" || answer.state === "STARTED") &&
            (answer.tries || 0) <= state.interviewInfo.questionMaxAttempts
        );

        if (canAnswer) {
          break; // Esta es la próxima pregunta disponible
        } else {
          newCurrentQuestion += 1; // Saltar preguntas que excedieron intentos
        }
      }
    }
    setState((prev) => ({ ...prev, currentQuestion: newCurrentQuestion }));
  };
  
  const value = {
    ...state,
    updateState,
    updateCurrentQuestion,
  };

  return (
    <ProcessContext.Provider value={value}>{children}</ProcessContext.Provider>
  );
};
